import { rowScalar } from '../../encounter-map/ft3d/perception';
import { _ftCamField } from '../../shared/campaign-field';
import { itemsCol } from '../../shared/array-types';
import { isFt3dRulerFrame, type Ft3dRulerFrame } from '../../shared/ft3d-ruler-frame';
type Row = Record<string, any>;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

export interface PresentationContext {
    encounterId: string; campaignId: string; userId: string; actorId: string;
    canManage: boolean; encounter: Row | null; campaign: Row | null; displayName?: string;
}
export function presentationActor(ctx: PresentationContext): Row | null {
    const rows = ctx.encounter?.encounter_creatures_arr ?? [];
    return rows.find((row: Row) => upper(rowScalar(row, 'encounter_creature_id')) === upper(ctx.actorId)
        && row.permissions?.player === true)
        ?? (!ctx.canManage ? rows.find((row: Row) => row.permissions?.player === true) : null) ?? null;
}
/** Ping and ruler share the roster colour and speaker identity, including the GM's selected NPC. */
export function presentationIdentity(ctx: PresentationContext, actor = presentationActor(ctx)): Row {
    const users = new Set([upper(_ftCamField(ctx.campaign, 'campaign_gm'))]);
    for (const row of ctx.campaign?.campaign_players ?? []) for (const key of ['player_character_created_by_id', 'permissioned']) {
        const id = upper(rowScalar(row, key)); if (id) users.add(id);
    }
    // Stable roster ordering gives every member its own hue. An account outside the roster (a site admin
    // looking in) takes a slot after every member, so it never shares a member's colour; inserting it into
    // the sorted roster used to shift it onto the GM's slot.
    const roster = [...users].filter(Boolean).sort();
    const member = roster.indexOf(upper(ctx.userId));
    const outsider = [...upper(ctx.userId)].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 7) % 5;
    const index = member >= 0 ? member : roster.length + outsider;
    const hue = index * 137.507764 % 360;
    const channel = (offset: number): string => {
        let t = hue / 360 + offset; if (t < 0) t += 1; if (t > 1) t -= 1;
        const p = 0.288, q = 0.912;
        const value = t < 1 / 6 ? p + (q - p) * 6 * t : t < 1 / 2 ? q : t < 2 / 3 ? p + (q - p) * (2 / 3 - t) * 6 : p;
        return Math.round(value * 255).toString(16).padStart(2, '0');
    };
    const speaker = ctx.canManage && itemsCol(actor, 'item_type') === 'player_character' ? null : actor;
    return {
        actor_id: actor ? upper(rowScalar(actor, 'encounter_creature_id')) : '',
        color: `#${channel(1 / 3)}${channel(0)}${channel(-1 / 3)}`, from_user_id: ctx.userId,
        from_kind: speaker ? (itemsCol(speaker, 'item_type') === 'player_character' ? 'player_character' : 'encounter_creature') : ctx.canManage ? 'gm' : 'user',
        from_entity_id: speaker ? rowScalar(speaker, 'encounter_creature_id') : null,
        from_display_name: speaker ? rowScalar(speaker, 'encounter_creature_name') || rowScalar(speaker, 'player_name') || itemsCol(speaker, 'item_name') || 'Someone'
            : ctx.displayName || _ftCamField(ctx.campaign, 'gm_username') || (ctx.canManage ? 'Game Master' : 'Someone'),
        from_thumbnail_url: null };
}
export function pingFrame(ctx: PresentationContext, request: Row, now: number, last: number): Row {
    if (!ctx.encounterId || !ctx.campaignId || !ctx.userId) throw new Error('ping:context-required');
    const actor = presentationActor(ctx);
    if (!ctx.canManage && !actor && !ctx.campaign?.campaign_players?.some((row: Row) =>
        ['player_character_created_by_id', 'permissioned'].some(key => upper(rowScalar(row, key)) === upper(ctx.userId))))
        throw new Error('ping:not-at-table');
    if (now - last < 1000) throw new Error('Please wait before pinging again');
    const point = { x: Number(request.x), y: Number(request.y ?? 0), z: Number(request.z) };
    if (!Object.values(point).every(Number.isFinite) || Math.max(...Object.values(point).map(Math.abs)) > 100000)
        throw new Error('Invalid FT3D ping position');
    const { actor_id, ...identity } = presentationIdentity(ctx, actor);
    return { type: 'ft3d_ping', schema: 'ft.ft3d.encounter-ping.v1', space: 'ft3d', actor_id,
        encounter_id: ctx.encounterId, campaign_id: ctx.campaignId, ...point,
        ping_id: crypto.randomUUID().toUpperCase(), operation_id: crypto.randomUUID(), ...identity,
        target: request.targetId ? { kind: request.targetKind || 'other', id: upper(request.targetId) } : null, ts: now / 1000 };
}
export function rulerFrame(ctx: PresentationContext, body: Row, now = Date.now()): Ft3dRulerFrame {
    if (!ctx.encounterId || !ctx.campaignId || !ctx.userId) throw new Error('ruler:context-required');
    const actor = presentationActor(ctx);
    if (!ctx.canManage && !actor && !ctx.campaign?.campaign_players?.some((row: Row) =>
        ['player_character_created_by_id', 'permissioned'].some(key => upper(rowScalar(row, key)) === upper(ctx.userId))))
        throw new Error('ruler:not-at-table');
    const frame = { type: body.action === 'clear' ? 'ft3d_ruler_clear' : 'ft3d_ruler', schema: 'ft.ft3d.ruler.v1', space: 'ft3d',
        encounter_id: ctx.encounterId, campaign_id: ctx.campaignId, operation_id: crypto.randomUUID(),
        ruler_id: body.ruler_id, from_client_id: body.from_client_id, sequence: body.sequence,
        points: body.action === 'clear' ? [] : body.points, finished: body.finished === true,
        reach_ft: body.reach_ft ?? 5, origin_size: body.origin_size ?? null, final_size: body.final_size ?? null,
        ...presentationIdentity(ctx, actor), ts: now / 1000 };
    if (!isFt3dRulerFrame(frame)) throw new Error('ruler:invalid-frame');
    return frame;
}
export function spotlightFrame(ctx: PresentationContext, body: Row): Row {
    if (!ctx.canManage) throw new Error('Only the GM can use the Director Spotlight.');
    if (!ctx.encounterId || !body.spotlight_id || !Number.isSafeInteger(body.sequence) || body.sequence < 0)
        throw new Error('Invalid spotlight identity');
    if (!['update', 'clear'].includes(body.action)) throw new Error('Invalid spotlight action');
    const frame: Row = { type: `ft3d_director_spotlight_${body.action}`, schema: 'ft.ft3d.director-spotlight.v1',
        actor_id: '', encounter_id: ctx.encounterId, operation_id: crypto.randomUUID(),
        spotlight_id: body.spotlight_id, sequence: body.sequence, from_user_id: ctx.userId, ts: Date.now() / 1000 };
    if (body.action === 'update') {
        for (const axis of ['x', 'y', 'z']) {
            const value = Number(body[axis]);
            if (!Number.isFinite(value) || Math.abs(value) > 100000) throw new Error('Invalid spotlight position');
            frame[axis] = value;
        }
        if (!Number.isFinite(Number(body.radius_ft))) throw new Error('Invalid spotlight radius');
        frame.radius_ft = Math.max(0.5, Math.min(60, Number(body.radius_ft)));
    }
    return frame;
}
